'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@extern/ui/avatar';
import { Button } from '@extern/ui/button';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaHeaderClose,
  CredenzaTitle,
} from '@extern/ui/credenza';
import { NumberIcon } from '@extern/ui/number-icon';
import { Text } from '@extern/ui/text';
import Image from 'next/image';

import type { RecommendationItem } from '@/features/recommendations/_api/recomendations.schema';
import { getNameInitials } from '@/utils/get-name-initials';

interface ExternshipPreviewWidgetModalProps {
  externship: RecommendationItem | null;
  onOpenChange: (open: boolean) => void;
  onStartNow?: () => void;
  open: boolean;
}

export function ExternshipPreviewWidgetModal({
  open,
  onOpenChange,
  onStartNow,
  externship,
}: ExternshipPreviewWidgetModalProps) {
  if (externship === null) {
    return null;
  }

  return (
    <Credenza onOpenChange={onOpenChange} open={open}>
      <CredenzaContent className="w-full max-w-xl sm:max-w-xl md:max-w-xl">
        <CredenzaHeader className="flex flex-col gap-3" hideClose>
          <div className="flex w-full items-center gap-2">
            <Avatar className="border border-role-outline-subtle" size="48">
              <AvatarImage
                alt={externship.companyName || 'Company logo'}
                className="object-cover"
                src={externship.companyLogo ? externship.companyLogo : '/images/externship-image-placeholder.svg'}
              />
              <AvatarFallback size="48" variant="initials">
                {getNameInitials(externship.companyName)}
              </AvatarFallback>
            </Avatar>
            <CredenzaTitle className="flex-1">{externship.title}</CredenzaTitle>
            <CredenzaHeaderClose />
          </div>
        </CredenzaHeader>
        <CredenzaBody className="flex flex-col gap-4 overflow-y-auto md:max-h-[65vh]">
          <CredenzaDescription className="text-ellipsis text-role-on-background">
            {externship.details?.overview}
          </CredenzaDescription>
          <div className="relative min-h-44 w-full overflow-hidden rounded-2xl">
            <Image
              alt={externship.title}
              className="block object-cover"
              fill
              src={externship?.details?.topImage || externship?.image || '/images/externship-image-placeholder.svg'}
            />
          </div>

          <div className="flex flex-col gap-4">
            <Text className="text-role-on-background-subtle" title="md">
              What you will learn and accomplish
            </Text>
            <ul>
              {((externship?.details?.responsibilities || []) as { title: string; description: string }[]).map(
                (responsibility, index) => (
                  <li className="flex items-start justify-start gap-2 pb-4" key={responsibility.title}>
                    <div className="flex min-h-8 min-w-8 items-center justify-center rounded-full bg-role-disabled">
                      <NumberIcon index={index + 1} size={16} />
                    </div>
                    <div className="flex flex-col">
                      <Text title="sm">{responsibility.title}</Text>
                      <Text body="lg">{responsibility.description}</Text>
                    </div>
                  </li>
                ),
              )}
            </ul>
          </div>
        </CredenzaBody>
        {onStartNow ? (
          <CredenzaFooter className="flex justify-center">
            <Button appearance="outlined" className="w-full" onClick={onStartNow}>
              Start now
            </Button>
          </CredenzaFooter>
        ) : null}
      </CredenzaContent>
    </Credenza>
  );
}
